import React, { useState } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity } from "react-native";
import { router } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedDropDown } from "@/components/ThemedDropDown";
import { ThemedSegmentedControl } from "@/components/ThemedSegmentedControl";
import { ThemedToggle } from "@/components/ThemedToggle";
import { ThemedRating } from "@/components/ThemedRating";
import { ThemedPanel } from "@/components/ThemedPanel";
import { CapturePanel } from "@/components/CapturePanel";
import { CustomAlert } from "@/components/ThemedAlert";
import { ComponentRefTag } from "@/components/design/ComponentRefTag";
import { ThemedActionHoverPanel } from "@/components/ThemedActionHoverPanel";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Colors, BrandColors, Spacing, Fonts, CurrentPlatformTheme, CardStyles } from "@/constants/ThemeColours";
import { useColorScheme } from "@/hooks/useColorScheme";
import { Ionicons } from "@expo/vector-icons";
import type { MemoryModel, ContactModel, GroupModel, MediaModel, ITagAssociationMeta, IDate } from "common247";
import { MemoryCard } from "@/components/memory/MemoryCard";
import { ContactCard } from "@/components/contacts/ContactCard";
import { GroupCard } from "@/components/groups/GroupCard";
import { ThemedChip } from "@/components/ThemedChip";
import { ThemedPlatformSymbol, PLATFORM_SYMBOLS, type PlatformSymbolType } from "@/components/ThemedPlatformSymbol";

const SORT_OPTIONS = [
  "Date (Newest First)",
  "Date (Oldest First)",
  "Title (A–Z)",
  "Archival Priority",
];

const CATEGORY_OPTIONS: { label: string; icon: keyof typeof Ionicons.glyphMap; color: string }[] = [
  { label: "Family Album", icon: "images-outline", color: BrandColors.sungoldOrange.hex },
  { label: "Audio Tape", icon: "mic-outline", color: BrandColors.beetRed.hex },
  { label: "Heirloom Photo", icon: "camera-outline", color: BrandColors.grassyGreen.hex },
  { label: "Historical Document", icon: "document-text-outline", color: BrandColors.blueLinen.hex },
];

const DEMO_MEMORY: MemoryModel = {
  id: "demo-mem-1",
  title: "1974 Pacific Coast Highway Expedition",
  description: "Field voice recordings, route maps, and handwritten journal letters.",
  date: "1974-08-15",
  location: { text: "Big Sur, California" },
  ownerId: "demo-user",
  ownerDisplayName: "Mara Solis",
  userRole: "owner",
  canShare: true,
  mediaIds: ["demo-media-1", "demo-media-2"],
  createdAt: Date.parse("1974-08-15T10:00:00.000Z"),
};

const DEMO_CONTACT: ContactModel = {
  id: "demo-contact-1",
  displayName: "Evelyn Vance",
  email: "evelyn.vance@archival-heritage.org",
  isActive: true,
  userRole: "owner",
  canShare: true,
  ownerId: "demo-user",
  createdAt: Date.parse("2026-08-10T12:00:00Z"),
};

const DEMO_GROUP: GroupModel = {
  id: "demo-group-1",
  name: "Pacific Ridge Oral Archivists",
  description: "Collaborative research collective preserving 20th century oral histories.",
  ownerId: "demo-user",
  contactIds: [],
  userRole: "owner",
  canShare: true,
  createdAt: Date.parse("2026-08-10T12:00:00Z"),
};

const DEMO_MEDIA: MediaModel = {
  id: "demo-media-1",
  filename: "1974-slide-sunset.jpg",
  url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=500&auto=format&fit=crop&q=60",
  mediaType: "image",
  ownerId: "demo-user",
  userRole: "owner",
  canShare: true,
  createdAt: Date.parse("1974-08-15T10:00:00.000Z"),
} as any;

const DEMO_TAG = {
  id: "demo-tag-1",
  name: "Coastal Highway",
  tagType: "memory",
};

const DEMO_SHARE = {
  id: "demo-share-1",
  targetId: "demo-mem-1",
  targetType: "memory" as const,
  recipientUserId: "demo-user-2",
  recipientDisplayName: "Arthur Pendelton",
  sharedItem: {
    entityType: "memory" as const,
    id: "demo-mem-1",
    title: "1974 Pacific Coast Highway Expedition",
    date: "1974-08-15",
  },
};

/**
 * Design Style Guide Showcase Screen.
 * Demonstrates compact spacing, read-only vs editable control states, button variants,
 * color tokens, input boxes, dropdown selectors, and archival frames.
 */
export default function DesignStyleScreen() {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";
  const colors = Colors[isDark ? "dark" : "light"];

  const [cardDensity, setCardDensity] = useState<"standard" | "compact">("standard");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [sampleText, setSampleText] = useState("Alexandre Dumas");
  const [sampleEmail, setSampleEmail] = useState("alexandre.dumas@example.com");
  const [sampleNotes, setSampleNotes] = useState(
    "Collaborative travel notes with itinerary details and shared memories across Kyoto and Osaka.",
  );

  // Input boxes showcase states
  const [demoInputTitle, setDemoInputTitle] = useState("Summer Solstice Gathering");
  const [demoSearchQuery, setDemoSearchQuery] = useState("");
  const [demoErrorVal, setDemoErrorVal] = useState("");
  const [demoValidVal, setDemoValidVal] = useState("archivist@family-archive.org");
  const [demoJournalText, setDemoJournalText] = useState("Handwritten letter discovered in cedar chest, dated September 1942.");

  // Dropdown showcase states
  const [activeSort, setActiveSort] = useState("Date (Newest First)");
  const [activeCategory, setActiveCategory] = useState("Family Album");
  const [demoRole, setDemoRole] = useState("viewer");

  // Toggle showcase states
  const [toggleBasic, setToggleBasic] = useState(true);
  const [toggleWithSublabel, setToggleWithSublabel] = useState(false);
  const [toggleDynamic, setToggleDynamic] = useState(true);

  // Rating showcase states
  const [demoRatingUnselected, setDemoRatingUnselected] = useState<number>(0);
  const [demoRatingSelected, setDemoRatingSelected] = useState<number>(4);

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");

  return (
    <ThemedScreen
      title="Design Style Guide"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 8 }}>
          ARCHIVAL DESIGN SPECIFICATIONS — COMPACT SPACING & INTER TYPOGRAPHY
        </ThemedText>

        {/* Interactive Mode Sandbox */}
        <ThemedView style={[styles.card, styles.interactiveCard, { backgroundColor: cardBg, borderColor, borderLeftColor: tintColor }]}>
          <View style={styles.sandboxHeader}>
            <View style={{ flex: 1 }}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 2 }}>
                <ThemedText type="defaultSemiBold" style={{ fontSize: 14 }}>
                  Interactive Form Mode
                </ThemedText>
                <ComponentRefTag name="REF: <StateModeToggle>" />
              </View>
              <ThemedText style={{ fontSize: 12, color: textMuted }}>
                Toggle between editable (Butter Bean) and non-editable (darker Butter Bean)
              </ThemedText>
            </View>
            <ThemedToggle
              value={isReadOnly}
              onValueChange={setIsReadOnly}
              activeLabel="Read-Only"
              inactiveLabel="Editable"
            />
          </View>

          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>Display Name</ThemedText>
              <ComponentRefTag name="REF: <ThemedInput:Standard>" />
            </View>
            <ThemedInput
              placeholder="Full name"
              value={sampleText}
              onChangeText={setSampleText}
              editable={!isReadOnly}
            />
          </View>

          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>Email Address</ThemedText>
              <ComponentRefTag name="REF: <ThemedInput:Email>" />
            </View>
            <ThemedInput
              placeholder="email@example.com"
              value={sampleEmail}
              onChangeText={setSampleEmail}
              editable={!isReadOnly}
              keyboardType="email-address"
            />
          </View>

          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>Memory Notes (Multiline)</ThemedText>
              <ComponentRefTag name="REF: <ThemedInput:Multiline>" />
            </View>
            <ThemedInput
              placeholder="Enter notes..."
              value={sampleNotes}
              onChangeText={setSampleNotes}
              editable={!isReadOnly}
              multiline
              numberOfLines={3}
            />
          </View>

          <View style={styles.stateNotice}>
            <Ionicons
              name={isReadOnly ? "lock-closed-outline" : "create-outline"}
              size={14}
              color={isReadOnly ? textMuted : tintColor}
            />
            <ThemedText style={[styles.stateNoticeText, { color: isReadOnly ? textMuted : tintColor }]}>
              {isReadOnly
                ? `Non-editable / read-only active (darker Butter Bean: ${colors.inputReadOnly})`
                : `Editable active (Butter Bean: ${colors.inputEditable})`}
            </ThemedText>
          </View>

          <View style={[styles.stateNotice, { marginTop: 6, backgroundColor: "rgba(161, 174, 209, 0.15)" }]}>
            <Ionicons name="phone-portrait-outline" size={14} color={tintColor} />
            <ThemedText style={[styles.stateNoticeText, { color: textMuted }]}>
              Platform Style: <ThemedText type="defaultSemiBold" style={{ color: colors.text }}>{CurrentPlatformTheme.name.toUpperCase()}</ThemedText> • Input Forecolour: <ThemedText type="defaultSemiBold" style={{ color: colors.inputText }}>{colors.inputText}</ThemedText>
            </ThemedText>
          </View>
        </ThemedView>

        {/* Compact Buttons Section */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Buttons & Actions (Blue Linen Palette)
          </ThemedText>
          <ThemedText style={styles.descText}>
            Harmonized in the archival Blue Linen range (Pantone 7681 U) with tightened 8px vertical / 16px horizontal padding and crisp 4px architectural radius.
          </ThemedText>

          <View style={styles.buttonGrid}>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:Primary>" />
              <ThemedButton title="Primary Action" variant="primary" onPress={() => { }} />
            </View>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:Secondary>" />
              <ThemedButton title="Secondary Action" variant="secondary" onPress={() => { }} />
            </View>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:Minimal>" />
              <ThemedButton title="Minimal Button" variant="minimal" onPress={() => { }} />
            </View>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:Danger>" />
              <ThemedButton title="Danger / Delete" variant="danger" onPress={() => { }} />
            </View>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:Disabled>" />
              <ThemedButton title="Disabled Button" variant="primary" disabled onPress={() => { }} />
            </View>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:WithIcon>" />
              <ThemedButton
                title="Action with Icon"
                icon={<Ionicons name="camera-outline" size={16} color="#FFFFFF" />}
                variant="primary"
                onPress={() => { }}
              />
            </View>
            <View style={styles.buttonRefCol}>
              <ComponentRefTag name="REF: <ThemedButton:Loading>" />
              <ThemedButton
                title="Click to Test Progress Bar"
                variant="primary"
                onPress={async () => {
                  await new Promise((resolve) => setTimeout(resolve, 2500));
                }}
              />
            </View>
          </View>

          {/* Save & Cancel Horizontal Action Row Standard */}
          <View style={styles.actionRowContainer}>
            <View style={styles.frameHeaderWithRef}>
              <ThemedText style={styles.subSectionTitle}>
                Save / Update & Cancel Layout Standard:
              </ThemedText>
              <ComponentRefTag name="REF: <HorizontalActionRow>" />
            </View>
            <ThemedText style={styles.subSectionDesc}>
              Positioned on the same horizontal row, gracefully wrapping vertically only when constrained.
            </ThemedText>
            <View style={styles.actionRow}>
              <ThemedButton title="Cancel" variant="minimal" onPress={() => { }} />
              <ThemedButton title="Save Changes" variant="primary" onPress={() => { }} />
            </View>
          </View>
        </ThemedView>

        {/* Media Capture & Drop Panel Showcase */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.frameHeaderWithRef}>
            <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
              Media Capture &amp; Drop Panel
            </ThemedText>
            <ComponentRefTag name="REF: <CapturePanel>" />
          </View>
          <ThemedText style={styles.descText}>
            Universal shared component providing drag-and-drop file ingestion with interactive drop area hover feedback, web file picker, and Photo, Video, and Audio controls built with &lt;ThemedButton&gt;.
          </ThemedText>

          <View style={{ marginTop: Spacing.two }}>
            <CapturePanel
              onUploadFiles={(files) => {
                CustomAlert.alert("CapturePanel Sandbox", `Received ${files.length} file(s) in sandbox.`);
              }}
            />
          </View>
        </ThemedView>

        {/* Segmented Controls Section */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Segmented Controls (Inline Roles & Permissions)
          </ThemedText>
          <ThemedText style={styles.descText}>
            For inline multi-state selections. When editable, the active segment uses primary tint. When read-only, the active selection's backcolour aligns with the Disabled Button backcolour.
          </ThemedText>

          <View style={styles.segmentedDemoRow}>
            <View style={styles.segmentedDemoCol}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText style={styles.segmentedStateLabel}>Editable Segmented Control:</ThemedText>
                <ComponentRefTag name="REF: <SegmentedControl:Editable>" />
              </View>
              <ThemedSegmentedControl
                value={demoRole}
                onChange={setDemoRole}
                options={[
                  { label: "Viewer", value: "viewer" },
                  { label: "Editor", value: "editor" },
                  { label: "Revoke", value: "revoke", destructive: true },
                ]}
              />
            </View>

            <View style={styles.segmentedDemoCol}>
              <View style={styles.frameHeaderWithRef}>
                <ThemedText style={styles.segmentedStateLabel}>Read-Only Segmented Control:</ThemedText>
                <ComponentRefTag name="REF: <SegmentedControl:ReadOnly>" />
              </View>
              <ThemedSegmentedControl
                value="viewer"
                readOnly
                onChange={() => { }}
                options={[
                  { label: "Viewer", value: "viewer" },
                  { label: "Editor", value: "editor" },
                ]}
              />
            </View>
          </View>
        </ThemedView>

        {/* Input Boxes & Field Architecture */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Input Boxes & Field Architecture
          </ThemedText>
          <ThemedText style={styles.descText}>
            Archival field architecture with crisp 4px architectural radius, high-legibility border contrast, dedicated labels, inline validation states, and distinct read-only shading.
          </ThemedText>

          {/* Standard Input */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <ThemedText style={styles.fieldLabel}>Archival Title (Standard Single-Line)</ThemedText>
                <ThemedText type="stamp" style={styles.stampAnnotation}>REQUIRED</ThemedText>
              </View>
              <ComponentRefTag name="REF: <ThemedInput:Standard>" />
            </View>
            <ThemedInput
              placeholder="e.g. Grandma's 90th Birthday"
              value={demoInputTitle}
              onChangeText={setDemoInputTitle}
            />
            <ThemedText style={styles.fieldHelperText}>
              Crisp 4px radius with 1px border. Focus activates primary brand tint.
            </ThemedText>
          </View>

          {/* Search Input with Leading Icon & Clear Action */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>Archive Search (Leading Icon &amp; Clear Action)</ThemedText>
              <ComponentRefTag name="REF: <ThemedInput:SearchClearable>" />
            </View>
            <View style={styles.filterInputWrapper}>
              <Ionicons name="search-outline" size={16} color={textMuted} style={styles.searchLeadingIcon} />
              <ThemedInput
                placeholder="Search transcripts, dates, tags..."
                value={demoSearchQuery}
                onChangeText={setDemoSearchQuery}
                style={styles.searchInputField}
              />
              {demoSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setDemoSearchQuery("")}
                  accessibilityLabel="Clear search text"
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  style={styles.searchClearBtn}
                >
                  <Ionicons name="close-circle" size={16} color={textMuted} />
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Validation States Grid */}
          <View style={styles.inputStatesRow}>
            {/* Error Validation State */}
            <View style={styles.inputStatesCol}>
              <View style={styles.fieldLabelRow}>
                <ThemedText style={styles.fieldLabel}>Validation: Error State</ThemedText>
                <ComponentRefTag name="REF: <ThemedInput:InlineError>" />
              </View>
              <ThemedInput
                placeholder="Required identifier"
                value={demoErrorVal}
                onChangeText={setDemoErrorVal}
                error={demoErrorVal.trim().length === 0 ? "Archive accession number is required" : null}
              />
            </View>

            {/* Success Validation State */}
            <View style={styles.inputStatesCol}>
              <View style={styles.fieldLabelRow}>
                <ThemedText style={styles.fieldLabel}>Validation: Verified State</ThemedText>
                <ComponentRefTag name="REF: <ThemedInput:InlineValid>" />
              </View>
              <ThemedInput
                placeholder="Email or Archivist ID"
                value={demoValidVal}
                onChangeText={setDemoValidVal}
                isValid={true}
              />
            </View>
          </View>

          {/* Read-Only State Display */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <ThemedText style={styles.fieldLabel}>Accession ID (Read-Only / Protected)</ThemedText>
                <View style={[styles.readOnlyBadge, { backgroundColor: colors.buttonDisabled }]}>
                  <Ionicons name="lock-closed" size={10} color={colors.buttonText} />
                  <ThemedText style={[styles.readOnlyBadgeText, { color: colors.buttonText }]}>LOCKED</ThemedText>
                </View>
              </View>
              <ComponentRefTag name="REF: <ThemedInput:ReadOnly>" />
            </View>
            <ThemedInput
              value="ACC-1942-SEP-08819-VOL3"
              editable={false}
            />
            <ThemedText style={styles.fieldHelperText}>
              Read-only uses Butter Bean shade ({colors.inputReadOnly}) to visually signal non-editable content.
            </ThemedText>
          </View>

          {/* Multiline Notes / Journal Input */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <ThemedText style={styles.fieldLabel}>Archival Ledger Notes (Multiline)</ThemedText>
                <ThemedText style={styles.charCountText}>{demoJournalText.length} characters</ThemedText>
              </View>
              <ComponentRefTag name="REF: <ThemedInput:Multiline>" />
            </View>
            <ThemedInput
              placeholder="Record historical context, oral story transcription..."
              value={demoJournalText}
              onChangeText={setDemoJournalText}
              multiline
              numberOfLines={3}
            />
          </View>
        </ThemedView>

        {/* Drop Down Boxes & Selectors */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Drop Down Boxes & Selectors
          </ThemedText>
          <ThemedText style={styles.descText}>
            Tailored selectors replacing browser defaults with 4px architectural curvature, stamp labels, explicit active indicators, and read-only locked modes.
          </ThemedText>

          {/* Standard Interactive Dropdown */}
          <View style={styles.dropdownDemoWrapper}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>Sort Order (Interactive Popout)</ThemedText>
              <ComponentRefTag name="REF: <DropdownSelector:Combobox>" />
            </View>
            <ThemedDropDown
              searchable
              leadingIcon="swap-vertical-outline"
              options={SORT_OPTIONS.map((opt) => ({ label: opt, value: opt }))}
              value={activeSort}
              onSelect={(opt) => setActiveSort(opt.value)}
              helperText="Tap trigger to toggle menu. Live search filters options. Selected item displays checkmark indicator and tint highlighting."
            />
          </View>

          {/* Category Selector with Icon Badges */}
          <View style={styles.dropdownDemoWrapper}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>Archive Category Selector</ThemedText>
              <ComponentRefTag name="REF: <CategorySelector:ArchiveTypes>" />
            </View>
            <ThemedDropDown
              options={CATEGORY_OPTIONS.map((cat) => ({
                label: cat.label,
                value: cat.label,
                icon: cat.icon,
                iconColor: cat.color,
              }))}
              value={activeCategory}
              onSelect={(opt) => setActiveCategory(opt.value)}
              helperText="Displays custom item icon badges and color tinting on options."
            />
          </View>

          {/* Read-Only / Disabled Dropdown */}
          <View style={styles.dropdownDemoWrapper}>
            <View style={styles.fieldLabelRow}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <ThemedText style={styles.fieldLabel}>Repository Access Tier (Read-Only)</ThemedText>
                <View style={[styles.readOnlyBadge, { backgroundColor: colors.buttonDisabled }]}>
                  <Ionicons name="lock-closed" size={10} color={colors.buttonText} />
                  <ThemedText style={[styles.readOnlyBadgeText, { color: colors.buttonText }]}>IMMUTABLE</ThemedText>
                </View>
              </View>
              <ComponentRefTag name="REF: <DropdownSelector:ReadOnly>" />
            </View>
            <ThemedDropDown
              readOnly
              leadingIcon="shield-checkmark-outline"
              options={[{ label: "Permanent Archival Custodian", value: "custodian" }]}
              value="custodian"
              onSelect={() => { }}
              helperText="Read-only selector presents a locked lock icon with shaded background and non-interactive touch target."
            />
          </View>
        </ThemedView>

        {/* ThemedToggle Switch Controls Showcase */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.fieldLabelRow}>
            <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
              Toggle Switch Controls
            </ThemedText>
            <ComponentRefTag name="REF: <ThemedToggle>" />
          </View>
          <ThemedText style={styles.descText}>
            Unified boolean toggle switch adhering to platform tokens. Replaces ad-hoc switch wrappers with full accessibility, tap-anywhere labeling, dynamic state text, and disabled states.
          </ThemedText>

          {/* 1. Basic Toggle */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>1. Standard Toggle with Label</ThemedText>
              <ComponentRefTag name="REF: <ThemedToggle:Standard>" />
            </View>
            <ThemedToggle
              label="Allow Push Notifications"
              value={toggleBasic}
              onValueChange={setToggleBasic}
            />
          </View>

          {/* 2. Toggle with Description Sublabel */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>2. Toggle with Descriptive Sublabel</ThemedText>
              <ComponentRefTag name="REF: <ThemedToggle:WithSublabel>" />
            </View>
            <ThemedToggle
              label="Automatic Cloud Synchronization"
              sublabel="Sync memories and media changes across devices when connected to Wi-Fi"
              value={toggleWithSublabel}
              onValueChange={setToggleWithSublabel}
            />
          </View>

          {/* 3. Dynamic Active/Inactive Labels */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>3. Dynamic State Labels</ThemedText>
              <ComponentRefTag name="REF: <ThemedToggle:DynamicLabel>" />
            </View>
            <ThemedToggle
              activeLabel="High Precision GPS Enabled"
              inactiveLabel="Standard Location Tagging"
              value={toggleDynamic}
              onValueChange={setToggleDynamic}
            />
          </View>

          {/* 4. Disabled State */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>4. Disabled Toggle (Read-Only)</ThemedText>
              <ComponentRefTag name="REF: <ThemedToggle:Disabled>" />
            </View>
            <ThemedToggle
              label="Audit Log Archival (Mandatory)"
              sublabel="Managed by organization policy"
              value={true}
              disabled
              onValueChange={() => {}}
            />
          </View>
        </ThemedView>

        {/* Rating Controls (ThemedRating) */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Rating Controls (<ThemedText type="code">ThemedRating</ThemedText>)
          </ThemedText>
          <ThemedText style={styles.descText}>
            Interactive 1–5 star rating controls adhering to Memories button hover standards and unselected starting states. Supports hover preview, custom scale labels, and read-only states.
          </ThemedText>

          {/* 1. Unselected Starting State */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>1. Unselected Initial State (Starts with 0 stars)</ThemedText>
              <ComponentRefTag name="REF: <ThemedRating:Unselected>" />
            </View>
            <ThemedRating
              value={demoRatingUnselected}
              onChange={setDemoRatingUnselected}
              unselectedLabel="Click a star to rate"
            />
            <ThemedText style={styles.fieldHelperText}>
              Starts empty. Hovering over any star highlights up to that rating and previews the description before committing.
            </ThemedText>
          </View>

          {/* 2. Rated Active State */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>2. Interactive with Pre-selected Rating</ThemedText>
              <ComponentRefTag name="REF: <ThemedRating:Standard>" />
            </View>
            <ThemedRating
              value={demoRatingSelected}
              onChange={setDemoRatingSelected}
            />
          </View>

          {/* 3. Read-Only Display Rating */}
          <View style={styles.formGroup}>
            <View style={styles.fieldLabelRow}>
              <ThemedText style={styles.fieldLabel}>3. Read-Only Archival Score (Non-interactive)</ThemedText>
              <ComponentRefTag name="REF: <ThemedRating:ReadOnly>" />
            </View>
            <ThemedRating
              value={5}
              readOnly
              size={24}
              label="Archival Condition Score:"
            />
          </View>
        </ThemedView>

        {/* Platform Symbols (UX Standard 15) */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
              Platform Symbols (Status Indicators & Semantic Affordances)
            </ThemedText>
            <ComponentRefTag name="REF: <ThemedPlatformSymbol>" />
          </View>
          <ThemedText style={styles.descText}>
            Universal status indicators indicating item ownership, 5-star rating, auto-save state, sharing status, physical heirloom tracking, read-only permissions, and offline synchronization. Hover each icon to trigger its canonical tooltip.
          </ThemedText>

          {/* Quick row preview of all 7 symbols */}
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, marginVertical: 8, alignItems: "center" }}>
            <ThemedPlatformSymbol type="owner" size="medium" showLabel />
            <ThemedPlatformSymbol type="fivestar" size="medium" showLabel />
            <ThemedPlatformSymbol type="saved" size="medium" showLabel />
            <ThemedPlatformSymbol type="shared" size="medium" showLabel />
            <ThemedPlatformSymbol type="heirloom" size="medium" showLabel />
            <ThemedPlatformSymbol type="readonly" size="medium" showLabel />
            <ThemedPlatformSymbol type="synced" size="medium" showLabel />
          </View>

          <TouchableOpacity
            style={[styles.redirectCard, { borderColor: tintColor, backgroundColor: "rgba(150, 150, 150, 0.05)", marginTop: 6 }]}
            onPress={() => router.push("/admin/design/symbols" as any)}
            activeOpacity={0.75}
          >
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
              <View style={{ flex: 1 }}>
                <ThemedText type="defaultSemiBold" style={{ fontSize: 13, color: tintColor }}>
                  Open Dedicated "Platform Symbols" Design Workbench →
                </ThemedText>
                <ThemedText style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>
                  Interactive size matrix (small, medium, large), AutoSaveIndicator live testing, and item card trays.
                </ThemedText>
              </View>
              <Ionicons name="open-outline" size={18} color={tintColor} />
            </View>
          </TouchableOpacity>
        </ThemedView>

        {/* Frames & Enclosures */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Frames & Enclosures
          </ThemedText>
          <ThemedText style={styles.descText}>
            Architectural frames inspired by specimen mounts, archival ledgers, debossed catalog trays, and folio records. Crafted with crisp 4px geometry rather than generic AI bubble cards.
          </ThemedText>

          {/* 1. Archival Paper Frame / Specimen Mount */}
          <View style={styles.frameDemoItem}>
            <View style={styles.frameHeaderWithRef}>
              <ThemedText style={styles.frameTypeLabel}>1. Archival Paper Frame (Specimen Mount)</ThemedText>
              <ComponentRefTag name="REF: <ThemedChip:Archival>" />
            </View>
            <ThemedText style={styles.frameDescSubtext}>
              Specimen mount aesthetic featuring warm parchment backing, crisp 4px geometry, and universal Tier 1 &lt;ThemedChip&gt; classification tokens.
            </ThemedText>
            <View
              style={{
                borderWidth: 1,
                borderColor: isDark ? "#443F2D" : "#E8E2BC",
                borderRadius: 4,
                overflow: "hidden",
                backgroundColor: cardBg,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderBottomWidth: 1,
                  borderBottomColor: isDark ? "#443F2D" : "#E8E2BC",
                  backgroundColor: isDark ? "rgba(253, 250, 216, 0.05)" : "rgba(253, 250, 216, 0.35)",
                }}
              >
                <View
                  style={{
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: "rgba(71, 81, 11, 0.3)",
                  }}
                >
                  <ThemedText type="stamp" style={{ fontSize: 10, fontWeight: "700", letterSpacing: 0.8 }}>
                    SPECIMEN #402
                  </ThemedText>
                </View>
                <ThemedText style={{ fontSize: 10, color: textMuted }}>
                  RECORDED: 1942-09-18
                </ThemedText>
              </View>

              <View style={{ padding: 12 }}>
                <ThemedText type="subtitle" style={{ fontSize: 16, marginBottom: 4 }}>
                  Handwritten Field Journal
                </ThemedText>
                <ThemedText style={{ fontSize: 12, lineHeight: 18, opacity: 0.75, marginBottom: 10 }}>
                  Original cedar chest ledger containing pressed wildflower specimens and daily weather records from the northern ridge.
                </ThemedText>

                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                  <ThemedChip entityType="archival" label="Natural History" />
                  <ThemedChip entityType="archival" label="Physical Heirloom" />
                  <ThemedChip entityType="memory" label="Cedar Chest 1942" />
                </View>
              </View>
            </View>
          </View>

          {/* 2. Double-Line Ledger Frame */}
          <View style={styles.frameDemoItem}>
            <View style={styles.frameHeaderWithRef}>
              <ThemedText style={styles.frameTypeLabel}>2. Ledger Double-Line Frame (Tabular Records)</ThemedText>
              <ComponentRefTag name="REF: <LedgerFrame>" />
            </View>
            <View style={[styles.ledgerOuterFrame, { borderColor: isDark ? "#554D35" : "#D4CCA6", backgroundColor: colors.card }]}>
              <View style={[styles.ledgerInnerFrame, { borderColor: isDark ? "#554D35" : "#D4CCA6" }]}>
                <View style={[styles.ledgerHeader, { backgroundColor: isDark ? "rgba(253, 250, 216, 0.05)" : "rgba(253, 250, 216, 0.7)", borderBottomColor: isDark ? "#554D35" : "#D4CCA6" }]}>
                  <ThemedText type="stamp" style={{ letterSpacing: 1.2 }}>LEDGER CATALOG // VOLUME VII</ThemedText>
                  <ThemedText style={{ fontSize: 10, fontFamily: Fonts?.mono, color: textMuted }}>REV: 2.1</ThemedText>
                </View>
                <View style={styles.ledgerRow}>
                  <ThemedText style={[styles.ledgerCellKey, { fontFamily: Fonts?.mono }]}>001-A</ThemedText>
                  <ThemedText style={styles.ledgerCellTitle}>Kodachrome Slide - Summer 1968</ThemedText>
                  <ThemedText style={[styles.ledgerCellStatus, { color: BrandColors.grassyGreen.hex }]}>Archived</ThemedText>
                </View>
                <View style={[styles.ledgerRow, { borderTopWidth: 1, borderTopColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }]}>
                  <ThemedText style={[styles.ledgerCellKey, { fontFamily: Fonts?.mono }]}>002-B</ThemedText>
                  <ThemedText style={styles.ledgerCellTitle}>Super 8 Reel - Seaside Boardwalk</ThemedText>
                  <ThemedText style={[styles.ledgerCellStatus, { color: BrandColors.sungoldOrange.hex }]}>Restoring</ThemedText>
                </View>
              </View>
            </View>
          </View>

          {/* 3. Debossed Inset Frame */}
          <View style={styles.frameDemoItem}>
            <View style={styles.frameHeaderWithRef}>
              <ThemedText style={styles.frameTypeLabel}>3. Debossed Inset Frame (Metadata Tray)</ThemedText>
              <ComponentRefTag name="REF: <DebossedFrame>" />
            </View>
            <View
              style={[
                styles.debossedFrame,
                {
                  backgroundColor: isDark ? "rgba(0, 0, 0, 0.35)" : "rgba(253, 250, 216, 0.45)",
                  borderColor: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(171, 23, 23, 0.18)",
                },
              ]}
            >
              <View style={styles.debossedHeader}>
                <Ionicons name="finger-print-outline" size={14} color={tintColor} />
                <ThemedText type="stamp" style={{ fontSize: 10, color: tintColor }}>PROVENANCE CHAIN</ThemedText>
              </View>
              <View style={styles.debossedGrid}>
                <View style={styles.debossedCol}>
                  <ThemedText style={styles.debossedKey}>ORIGINAL DONOR</ThemedText>
                  <ThemedText style={styles.debossedVal}>Evelyn Vance (Estate)</ThemedText>
                </View>
                <View style={styles.debossedCol}>
                  <ThemedText style={styles.debossedKey}>STORAGE CONDITION</ThemedText>
                  <ThemedText style={styles.debossedVal}>Acid-Free Solander Box</ThemedText>
                </View>
                <View style={styles.debossedCol}>
                  <ThemedText style={styles.debossedKey}>DIGITAL MASTER</ThemedText>
                  <ThemedText style={styles.debossedVal}>FLAC 96kHz / 24-bit</ThemedText>
                </View>
              </View>
            </View>
          </View>

          {/* 4. Accent Folio Frame */}
          <View style={styles.frameDemoItem}>
            <View style={styles.frameHeaderWithRef}>
              <ThemedText style={styles.frameTypeLabel}>4. Accent Folio Frame (Featured Highlight)</ThemedText>
              <ComponentRefTag name="REF: <AccentFolioFrame>" />
            </View>
            <View
              style={[
                styles.accentFolioFrame,
                {
                  backgroundColor: colors.card,
                  borderColor,
                  borderLeftColor: colors.featuredHighlight,
                },
              ]}
            >
              <View style={styles.accentFolioHeader}>
                <View style={[styles.accentTag, { backgroundColor: colors.featuredHighlightBg, borderWidth: 1, borderColor: "rgba(71, 81, 11, 0.2)" }]}>
                  <ThemedText style={[styles.accentTagText, { color: isDark ? colors.featuredHighlight : "#47510B" }]}>
                    FEATURED HEIRLOOM
                  </ThemedText>
                </View>
                <Ionicons name="star" size={14} color={colors.featuredHighlight} />
              </View>
              <ThemedText type="subtitle" style={{ fontSize: 15, marginBottom: 4 }}>
                The Voice Notes of Mara Solis
              </ThemedText>
              <ThemedText style={{ fontSize: 12, lineHeight: 18, opacity: 0.8 }}>
                Recorded on cassette tape during the 1974 trans-continental expedition. Includes unfiltered reflections and road songs.
              </ThemedText>
            </View>
          </View>
        </ThemedView>

        {/* Brand Palette (web/assets/Colours.png) */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Brand Palette (Ingested from Colours.png)
          </ThemedText>
          <ThemedText style={styles.descText}>
            Official mid-century botanical archive palette with Pantone references and hex codes.
          </ThemedText>

          <View style={styles.paletteGrid}>
            {Object.values(BrandColors).map((c) => (
              <View key={c.hex} style={styles.swatchItem}>
                <View
                  style={[
                    styles.swatchColor,
                    { backgroundColor: c.hex, borderWidth: c.hex === "#FDFAD8" ? 1 : 0, borderColor },
                  ]}
                />
                <ThemedText style={styles.swatchTitle}>{c.name}</ThemedText>
                <ThemedText style={styles.swatchPantone}>{c.pantone}</ThemedText>
                <ThemedText style={styles.swatchHex}>{c.hex}</ThemedText>
              </View>
            ))}
          </View>
        </ThemedView>

        {/* Active Theme Tokens */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Active Theme Tokens ({isDark ? "Dark Mode" : "Light Mode"})
          </ThemedText>
          <ThemedText style={styles.descText}>
            Tokens mapped dynamically into backgrounds, cards, primary buttons, and borders.
          </ThemedText>

          <View style={styles.paletteGrid}>
            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.tint, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Primary Tint</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.tint}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.button }]} />
              <ThemedText style={styles.swatchTitle}>Primary Button</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.button}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.inputEditable, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Editable (Butter Bean)</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.inputEditable}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.inputReadOnly, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Non-Editable (Darker)</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.inputReadOnly}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.card, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Card Surface</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.card}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.buttonDisabled }]} />
              <ThemedText style={styles.swatchTitle}>Disabled Button</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.buttonDisabled}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.background, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Background</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.background}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.buttonSecondary, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Secondary</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.buttonSecondary}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.border }]} />
              <ThemedText style={styles.swatchTitle}>Border</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.border}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.danger }]} />
              <ThemedText style={styles.swatchTitle}>Destructive</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.danger}</ThemedText>
            </View>

            <View style={styles.swatchItem}>
              <View style={[styles.swatchColor, { backgroundColor: colors.featuredHighlight, borderWidth: 1, borderColor }]} />
              <ThemedText style={styles.swatchTitle}>Featured Highlight</ThemedText>
              <ThemedText style={styles.swatchHex}>{colors.featuredHighlight}</ThemedText>
            </View>
          </View>
        </ThemedView>

        {/* Typography Scale */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
            Typography Hierarchy
          </ThemedText>

          <View style={styles.typeRowWithRef}>
            <ThemedText type="editorialTitle">Editorial Title (Archival Serif)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='editorialTitle'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="stamp">STAMP REFERENCE #042 // SYSTEM MONO</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='stamp'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="title">Title (24px / Bold)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='title'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="subtitle">Subtitle (17px / Bold)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='subtitle'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="defaultSemiBold">Default SemiBold (14px / 600)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='defaultSemiBold'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="default">Default Body Text (14px / 400)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='default'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="smallBold">Small Bold Label (13px / 700)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='smallBold'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="small">Small Secondary Caption (13px / 500)</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='small'>" />
          </View>
          <View style={styles.typeRowWithRef}>
            <ThemedText type="code">code.identifier // Monospace Token</ThemedText>
            <ComponentRefTag name="REF: <ThemedText type='code'>" />
          </View>
        </ThemedView>

        {/* Item Card Architecture & Kebab Action Menus Workbench */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.frameHeaderWithRef}>
            <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
              Item Card Architecture & Kebab Action Menus
            </ThemedText>
            <ComponentRefTag name="REF: <ThemedKebabMenu> • <MemoryCard> • <ContactCard> • <GroupCard>" />
          </View>
          <ThemedText style={styles.descText}>
            Consolidates action buttons (Share, Edit, Delete) across all entity cards into a unified 3-dot kebab menu. Right-aligned to reclaim up to 75% horizontal space and eliminate title crowding. Supports Standard vs Compact density styles.
          </ThemedText>

          {/* Density Segmented Control */}
          <View style={{ marginBottom: Spacing.two }}>
            <ThemedSegmentedControl
              value={cardDensity}
              onChange={(val) => setCardDensity(val as "standard" | "compact")}
              options={[
                { label: "Standard Density (16px)", value: "standard" },
                { label: "Compact Density (10px)", value: "compact" },
              ]}
            />
          </View>

          {/* Live Density Metrics Display */}
          <View
            style={[
              styles.densityMetricsContainer,
              { backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", borderColor },
            ]}
          >
            <View style={styles.densityMetricItem}>
              <ThemedText style={styles.densityMetricLabel}>Padding</ThemedText>
              <ThemedText style={styles.densityMetricValue}>{CardStyles[cardDensity].padding}px</ThemedText>
            </View>
            <View style={styles.densityMetricItem}>
              <ThemedText style={styles.densityMetricLabel}>Radius</ThemedText>
              <ThemedText style={styles.densityMetricValue}>{CardStyles[cardDensity].borderRadius}px</ThemedText>
            </View>
            <View style={styles.densityMetricItem}>
              <ThemedText style={styles.densityMetricLabel}>Gap</ThemedText>
              <ThemedText style={styles.densityMetricValue}>{CardStyles[cardDensity].gap}px</ThemedText>
            </View>
            <View style={styles.densityMetricItem}>
              <ThemedText style={styles.densityMetricLabel}>Title Size</ThemedText>
              <ThemedText style={styles.densityMetricValue}>{CardStyles[cardDensity].titleSize}px</ThemedText>
            </View>
            <View style={styles.densityMetricItem}>
              <ThemedText style={styles.densityMetricLabel}>Kebab Trigger</ThemedText>
              <ThemedText style={styles.densityMetricValue}>{cardDensity === "compact" ? "26px" : "30px"}</ThemedText>
            </View>
          </View>

          {/* Card Specimens */}
          <View style={{ gap: Spacing.two, marginTop: 4 }}>
            <View>
              <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 6, color: textMuted }}>
                SPECIMEN 1: MEMORY CARD ({cardDensity.toUpperCase()})
              </ThemedText>
              <MemoryCard
                memory={DEMO_MEMORY}
                compact={cardDensity === "compact"}
                onPress={() => {}}
                onEditPress={() => {}}
                onDelete={() => {}}
                onShare={() => {}}
              />
            </View>

            <View>
              <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 6, color: textMuted }}>
                SPECIMEN 2: CONTACT CARD ({cardDensity.toUpperCase()})
              </ThemedText>
              <ContactCard
                contact={DEMO_CONTACT}
                compact={cardDensity === "compact"}
                onEditPress={() => {}}
                onDelete={() => {}}
                onShare={() => {}}
              />
            </View>

            <View>
              <ThemedText type="stamp" style={{ fontSize: 10, letterSpacing: 1, marginBottom: 6, color: textMuted }}>
                SPECIMEN 3: GROUP CARD ({cardDensity.toUpperCase()})
              </ThemedText>
              <GroupCard
                group={DEMO_GROUP}
                compact={cardDensity === "compact"}
                onEditPress={() => {}}
                onDelete={() => {}}
                onShare={() => {}}
              />
            </View>
          </View>
        </ThemedView>

        {/* Item Cards: 5-Tier Consistency Architecture */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={styles.frameHeaderWithRef}>
            <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
              Item Cards: 5-Tier Architecture
            </ThemedText>
            <ComponentRefTag name="REF: <ThemedListAndCardWorkbench>" />
          </View>
          <ThemedText style={styles.descText}>
            Interactive test harnesses, specimens, and live configurations for the 5-Tier Item Card architecture (Chip, Compact, Medium, Full, Custom) have moved to the dedicated Themed List &amp; Card Workbench.
          </ThemedText>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: Spacing.two, marginTop: Spacing.one }}>
            <ThemedButton
              title="Open Themed List & Card Workbench"
              variant="secondary"
              onPress={() => router.push("/admin/design/themed-list-and-cards" as any)}
            />
          </View>
        </ThemedView>

        {/* ThemedPanel Architectural Component Showcase */}
        <ThemedPanel
          title="ThemedPanel Architectural Component"
          subtitle="Unified platform container enforcing sharp 90° corners, archival tactile drop shadows, and semantic variants."
          headerRight={<ComponentRefTag name="REF: <ThemedPanel>" />}
          elevation="low"
        >
          <ThemedText style={styles.descText}>
            ThemedPanel provides consistent structural containers across the application, replacing ad-hoc bordered views with guaranteed zero-radius corners and elevation depth.
          </ThemedText>

          <View style={{ gap: Spacing.two }}>
            {/* Specimen 1: Default Panel */}
            <ThemedPanel
              title="Standard Folio Mount"
              subtitle="Default variant with title, description, and action button"
              headerRight={
                <ThemedButton
                  title="Inspect"
                  variant="secondary"
                  onPress={() => {}}
                />
              }
              elevation="low"
            >
              <ThemedText style={{ fontSize: 13, lineHeight: 18 }}>
                Standard archival mounting with subtle 1px border and low elevation drop shadow.
              </ThemedText>
            </ThemedPanel>

            {/* Specimen 2: Accent Highlight Panel */}
            <ThemedPanel
              variant="accent"
              title="Featured Collection Folio"
              subtitle="Accent variant with 4px left highlight border"
              headerRight={<ComponentRefTag name="REF: <ThemedPanel:Accent>" />}
              elevation="medium"
            >
              <ThemedText style={{ fontSize: 13, lineHeight: 18 }}>
                Used for primary highlights, active workflows, and highlighted specimen records.
              </ThemedText>
            </ThemedPanel>

            {/* Specimen 3: Debossed Read-Only / Tray Panel */}
            <ThemedPanel
              variant="debossed"
              title="Archival Metadata Tray"
              subtitle="Debossed variant with Butter Bean parchment tint"
              headerRight={<ComponentRefTag name="REF: <ThemedPanel:Debossed>" />}
              elevation="none"
            >
              <ThemedText style={{ fontSize: 13, lineHeight: 18 }}>
                Non-editable data display, technical provenance, and immutable system records.
              </ThemedText>
            </ThemedPanel>

            {/* Specimen 4: High Elevation Modal/Floating Panel */}
            <ThemedPanel
              title="Floating Inspection Sheet"
              subtitle="High elevation profile for dialogs and popovers"
              headerRight={<ComponentRefTag name="REF: <ThemedPanel:High>" />}
              elevation="high"
            >
              <ThemedText style={{ fontSize: 13, lineHeight: 18 }}>
                Used for modal dialogs and elevated inspector sheets with deep 8px tactile shadow.
              </ThemedText>
            </ThemedPanel>
          </View>
        </ThemedPanel>

        {/* Floating Contextual Batch Action Bar */}
        <ThemedView style={[styles.card, { backgroundColor: cardBg, borderColor }]}>
          <View style={{ flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
            <View style={{ flex: 1, marginRight: 8 }}>
              <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                Floating Batch Action Bar (Contextual Toolbar)
              </ThemedText>
              <ThemedText style={styles.descText}>
                Contextual batch action bar rendered when one or more items are selected in lists and galleries across Memories, Media, Contacts, Groups, and Shares. Styled in signature Archival Blue Linen with high-contrast Obsidian Ink accents.
              </ThemedText>
            </View>
            <ComponentRefTag name="REF: <ThemedActionHoverPanel>" />
          </View>

          <View style={{ marginTop: 8, padding: 12, borderWidth: 1, borderColor, borderRadius: 6, backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" }}>
            <ThemedActionHoverPanel
              visible={true}
              floating={false}
              selectedCount={3}
              totalCount={12}
              onSelectAll={() => {}}
              onClearSelection={() => {}}
              actions={[
                { id: "share", label: "Share", icon: "share-social-outline", onPress: () => {} },
                { id: "tags", label: "Tags", icon: "pricetag-outline", onPress: () => {} },
                { id: "delete", label: "Delete", icon: "trash-outline", destructive: true, onPress: () => {} },
              ]}
            />
          </View>

          <View style={{ marginTop: 12, flexDirection: "row", justifyContent: "flex-end" }}>
            <ThemedButton
              title="Open Dedicated Action Hover Panel Workbench →"
              variant="primary"
              onPress={() => router.push("/admin/design/action-hover-panel" as any)}
            />
          </View>
        </ThemedView>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: Spacing.two,
    paddingBottom: Spacing.five,
    gap: Spacing.two,
  },
  card: {
    borderRadius: 0,
    borderWidth: 1,
    padding: Spacing.three,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  interactiveCard: {
    borderLeftWidth: 4,
    borderLeftColor: BrandColors.sungoldOrange.hex,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  sectionHeaderTitle: {
    fontSize: 18,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 15,
    marginBottom: 4,
  },
  descText: {
    fontSize: 12,
    opacity: 0.7,
    lineHeight: 17,
    marginBottom: Spacing.two,
  },
  sandboxHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: Spacing.two,
    paddingBottom: Spacing.one,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(150, 150, 150, 0.2)",
  },
  toggleDemoItem: {
    paddingVertical: Spacing.two,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(150, 150, 150, 0.2)",
  },
  toggleDemoHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: Spacing.one,
  },
  formGroup: {
    marginBottom: Spacing.one,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: "600",
    opacity: 0.7,
    marginBottom: 2,
  },
  stateNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: Spacing.one,
    paddingTop: Spacing.one,
  },
  stateNoticeText: {
    fontSize: 11,
    fontWeight: "600",
  },
  buttonGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.one,
  },
  buttonCol: {
    minWidth: 130,
    flex: 1,
  },
  buttonRefCol: {
    minWidth: 140,
    flex: 1,
    gap: 4,
  },
  actionRowContainer: {
    marginTop: Spacing.two,
    paddingTop: Spacing.two,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "rgba(150, 150, 150, 0.15)",
  },
  subSectionTitle: {
    fontSize: 13,
    fontWeight: "600",
    marginBottom: 2,
  },
  subSectionDesc: {
    fontSize: 11,
    opacity: 0.6,
    marginBottom: Spacing.one,
  },
  actionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: Spacing.two,
  },
  paletteGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.two,
  },
  swatchItem: {
    width: 95,
    alignItems: "center",
  },
  swatchColor: {
    width: "100%",
    height: 36,
    borderRadius: 4,
    marginBottom: 4,
  },
  swatchTitle: {
    fontSize: 10,
    fontWeight: "700",
    textAlign: "center",
  },
  swatchPantone: {
    fontSize: 8,
    opacity: 0.6,
    textAlign: "center",
  },
  swatchHex: {
    fontSize: 9,
    fontFamily: "monospace",
    opacity: 0.5,
    marginTop: 1,
  },
  typeRow: {
    paddingVertical: 3,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(150, 150, 150, 0.1)",
  },
  typeRowWithRef: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(150, 150, 150, 0.1)",
    gap: 8,
  },
  compactCard: {
    borderRadius: 0,
    borderWidth: 1,
    padding: Spacing.two,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  compactHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.one + 4,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  segmentedDemoRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.two,
    marginTop: Spacing.one,
  },
  segmentedDemoCol: {
    flex: 1,
    minWidth: 220,
  },
  segmentedStateLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 6,
    opacity: 0.8,
  },
  segmentedContainer: {
    flexDirection: "row",
    borderRadius: 4,
    padding: 2,
    alignSelf: "flex-start",
  },
  demoSegmentBtn: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  demoSegmentText: {
    fontSize: 12,
    fontWeight: "600",
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  stampAnnotation: {
    fontSize: 9,
    opacity: 0.65,
    letterSpacing: 0.8,
  },
  fieldHelperText: {
    fontSize: 11,
    opacity: 0.6,
    marginTop: 2,
    lineHeight: 15,
  },
  filterInputWrapper: {
    position: "relative",
    justifyContent: "center",
  },
  searchLeadingIcon: {
    position: "absolute",
    left: 10,
    zIndex: 2,
  },
  searchInputField: {
    paddingLeft: 34,
    paddingRight: 32,
    marginVertical: 0,
    height: 38,
    borderRadius: 4,
    fontSize: 13,
  },
  searchClearBtn: {
    position: "absolute",
    right: 8,
    zIndex: 2,
    padding: 2,
  },
  inputStatesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.two,
    marginVertical: Spacing.one,
  },
  inputStatesCol: {
    flex: 1,
    minWidth: 220,
  },
  readOnlyBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
  },
  readOnlyBadgeText: {
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.6,
  },
  charCountText: {
    fontSize: 10,
    opacity: 0.55,
  },
  dropdownDemoWrapper: {
    marginBottom: Spacing.two,
  },
  frameDemoItem: {
    marginBottom: Spacing.three,
  },
  frameHeaderWithRef: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 2,
  },
  frameTypeLabel: {
    fontSize: 13,
    fontWeight: "700",
    opacity: 0.85,
  },
  componentRefBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    backgroundColor: "rgba(150, 150, 150, 0.12)",
    alignSelf: "flex-start",
  },
  componentRefText: {
    fontSize: 10,
    letterSpacing: 0.2,
  },
  frameDescSubtext: {
    fontSize: 11,
    opacity: 0.65,
    marginBottom: 8,
  },
  ledgerOuterFrame: {
    borderWidth: 2,
    borderRadius: 4,
    padding: 3,
  },
  ledgerInnerFrame: {
    borderWidth: 1,
    borderRadius: 2,
    overflow: "hidden",
  },
  ledgerHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderBottomWidth: 1,
  },
  ledgerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 8,
  },
  ledgerCellKey: {
    fontSize: 11,
    opacity: 0.5,
    width: 45,
  },
  ledgerCellTitle: {
    fontSize: 12,
    fontWeight: "500",
    flex: 1,
  },
  ledgerCellStatus: {
    fontSize: 11,
    fontWeight: "700",
  },
  debossedFrame: {
    borderWidth: 1,
    borderRadius: 4,
    padding: 12,
  },
  debossedHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 10,
  },
  debossedGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.two,
  },
  debossedCol: {
    minWidth: 140,
    flex: 1,
  },
  debossedKey: {
    fontSize: 9,
    opacity: 0.55,
    marginBottom: 2,
    letterSpacing: 0.5,
  },
  debossedVal: {
    fontSize: 12,
    fontWeight: "600",
  },
  accentFolioFrame: {
    borderWidth: 1,
    borderLeftWidth: 4,
    borderRadius: 4,
    padding: 12,
  },
  accentFolioHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  accentTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 2,
  },
  accentTagText: {
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.8,
  },
  densityMetricsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    padding: 10,
    borderRadius: 8,
    marginBottom: Spacing.two,
    borderWidth: 1,
  },
  densityMetricItem: {
    flex: 1,
    minWidth: 70,
    alignItems: "center",
  },
  densityMetricLabel: {
    fontSize: 9,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.6,
    fontWeight: "bold",
    marginBottom: 2,
  },
  densityMetricValue: {
    fontSize: 12,
    fontWeight: "700",
  },
});
